import React, { useEffect } from "react";
import { withRouter } from "react-router-dom";
import PlusIcon from "../../../../assets/svg/icons/common/Plus";
import Breadcrumbs from "../../../../components/Breadcrumbs";
import DropDown from "../../../../components/DropDown/DropDown";
import SearchInput from "../../../../components/SearchInput/SearchInput";
import SlidingTab from "../../../../components/SlidingTab/SlidingTab";
import ActionBar from "../../../../components/action-bar";
import SkeletonLoader from "../../../../components/skeleton-loader";
import useCommonStore from "../../../../stores/common";
import useEquipmentStore, {
  debouncedFetchModels,
  equipmentDropdownOptions,
  fetchCategories,
  modelDropdownOptions,
} from "../../../../stores/equipment";
import useUserStore from "../../../../stores/user";
import AddEquipmentModal from "../../components/add-equipment-modal";
import DeleteEquipmentModal from "../../components/delete-equipment-modal";
import EquipmentBrandCard from "../../components/equipment-brand-card";
import EquipmentBrandCardLoader from "../../components/equipment-brand-card/loader";
import AddEquipmentBrandModal from "../../components/equipment-brand-modal";
import baseStyles from "../../styles.module.scss";
import styles from "./styles.module.scss";

const ModelsListing = ({ match, history }) => {
  const brandId = match?.params?.brand_id;
  const isV2Enabled = useUserStore((state) => state.isV2Enabled);
  const activeBrand = useEquipmentStore((state) => state.activeBrand);
  const activeModel = useEquipmentStore((state) => state.activeModel);
  const activeModelId = useEquipmentStore((state) => state.activeModelId);
  const setActiveModelId = useEquipmentStore((state) => state.setActiveModelId);
  const setActiveModel = useEquipmentStore((state) => state.setActiveModel);
  const setShowDetails = useCommonStore((state) => state.setShowDetails);
  const models = useEquipmentStore((state) => state.models);
  const modelsLoading = useEquipmentStore((state) => state.modelsLoading);
  const searchTerm = useEquipmentStore((state) => state.modelFilter.searchTerm);
  const setModelFilter = useEquipmentStore((state) => state.setModelFilter);
  const selectedModelIds = useEquipmentStore((state) => state.selectedModelIds);
  const categories = useEquipmentStore((state) => state.categories);
  const categoriesLoading = useEquipmentStore((s) => s.categoriesLoading);
  const selectedCategory = useEquipmentStore((s) => s.selectedCategory);
  const setSelectedCategory = useEquipmentStore((s) => s.setSelectedCategory);
  const addToSelectedModelIds = useEquipmentStore(
    (s) => s.addToSelectedModelIds,
  );
  const removeFromSelectedModelIds = useEquipmentStore(
    (s) => s.removeFromSelectedModelIds,
  );
  const clearSelectedModelIds = useEquipmentStore(
    (s) => s.clearSelectedModelIds,
  );
  const deleteModelModalOpen = useEquipmentStore((s) => s.deleteModelModalOpen);
  const setDeleteModelModalOpen = useEquipmentStore(
    (s) => s.setDeleteModelModalOpen,
  );
  const addBrandModalOpen = useEquipmentStore((s) => s.addBrandModalOpen);
  const setAddBrandModalOpen = useEquipmentStore((s) => s.setAddBrandModalOpen);
  const addEquipmentModalOpen = useEquipmentStore(
    (s) => s.addEquipmentModalOpen,
  );
  const setAddEquipmentModalOpen = useEquipmentStore(
    (s) => s.setAddEquipmentModalOpen,
  );

  useEffect(() => {
    const abortController = new AbortController();
    fetchCategories(abortController);
    const timeoutId = debouncedFetchModels(brandId, abortController);

    return () => {
      clearTimeout(timeoutId);
      abortController.abort();
    };
  }, [searchTerm, brandId, selectedCategory]);

  const handleModelClick = (model, event) => {
    if (
      event?.target?.closest("[data-checkbox]") ||
      event?.target?.closest("[data-dropdown]")
    ) {
      return;
    }

    console.log("model", model);

    setActiveModelId(model.id);
    setActiveModel(model);
    setShowDetails(true);
  };

  const handleModelCheckboxChange = (model) => {
    if (selectedModelIds.includes(model.id)) {
      removeFromSelectedModelIds(model.id);
    } else {
      addToSelectedModelIds(model.id);
    }
  };

  if (isNaN(brandId)) {
    return <div>Invalid brand id</div>;
  }

  const selectedModelText =
    selectedModelIds.length > 0
      ? `${selectedModelIds.length} ${
          selectedModelIds.length === 1 ? "model" : "models"
        } selected`
      : "";

  return (
    <div className={baseStyles.pageContainer}>
      <div className={baseStyles.header}>
        <div>
          <Breadcrumbs
            items={[
              {
                label: "Equipment",
                to: isV2Enabled ? "/equipments" : "/v2/equipments",
              },
              {
                label: activeBrand?.name,
              },
            ]}
          />
          <h1 className={baseStyles.pageHeading}>{activeBrand?.name}</h1>
        </div>

        <div className={baseStyles.headerActions}>
          <SearchInput
            onChange={(e) => {
              setModelFilter({
                searchTerm: e.target.value,
              });
            }}
            value={searchTerm}
          />
          <DropDown
            bgColor="#4C70EF"
            textColor="#FFFFFF"
            caretColor="#FFFFFF"
            smallFont
            options={equipmentDropdownOptions(history, isV2Enabled)}
            showArrow
          >
            <PlusIcon />
            Add
          </DropDown>
        </div>
      </div>

      {categoriesLoading && (
        <div className={styles.categoryContainer}>
          <SkeletonLoader variant="rectangular" width="300px" height="24px" />
        </div>
      )}

      {!categoriesLoading && categories.length > 0 && (
        <div className={styles.categoryContainer}>
          <SlidingTab
            onChange={(value) => setSelectedCategory(value)}
            options={[
              "All",
              "Mobile",
              "Basic",
              "Smart",
              "POS",
              "Gateway",
              "Add-on",
            ]}
            selectedTab={selectedCategory}
            noShortCodes
            noShrink
            fullWidth
          />
        </div>
      )}

      {!modelsLoading && models?.length === 0 && (
        <h2 className={baseStyles.searchResultsHeading}>
          {`No models found ${
            selectedCategory !== "All"
              ? `with category ${selectedCategory}`
              : ""
          } ${searchTerm ? `for "${searchTerm}"` : ""}`}
        </h2>
      )}

      <div className={baseStyles.cardGrid}>
        {modelsLoading &&
          Array.from({ length: 10 }).map((_, index) => (
            <EquipmentBrandCardLoader key={`equipment-brand-${index}`} />
          ))}

        {!modelsLoading &&
          models?.map((model) => (
            <EquipmentBrandCard
              key={`brand-${model.id}-${model.name}`}
              equipment={{
                id: model.id,
                name: model.equipment_name,
                image: `/get-image-by-path?path=${model.equipment_image}`,
                category: model?.category_name,
              }}
              onClick={(e) => handleModelClick(model, e)}
              checkboxOnChange={() => handleModelCheckboxChange(model)}
              showCheckbox
              isActive={activeModelId === model.id}
              isSelected={selectedModelIds.includes(model.id)}
              showDropdown
              dropdownOptions={modelDropdownOptions(model)}
            />
          ))}
      </div>

      <AddEquipmentBrandModal
        open={addBrandModalOpen}
        onClose={() => setAddBrandModalOpen(false)}
      />

      <AddEquipmentModal
        activeModel={activeModel}
        isEdit={!!activeModel}
        open={addEquipmentModalOpen}
        onClose={() => {
          setAddEquipmentModalOpen(false);
          setActiveModel(null);
          setActiveModelId(null);
        }}
        brandId={brandId}
      />

      <DeleteEquipmentModal
        open={deleteModelModalOpen}
        onClose={() => setDeleteModelModalOpen(false)}
      />

      <ActionBar
        visible={selectedModelIds.length > 0}
        text={selectedModelText}
        buttonText="Delete"
        onButtonClick={() => setDeleteModelModalOpen(true)}
        onCrossClick={() => clearSelectedModelIds()}
      />
    </div>
  );
};

export default withRouter(ModelsListing);
